import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import {
  FILTER_FEATURE,
  PARTNER_MANAGEMENT,
  USER_STAFF,
  USER_STORE,
} from "api/ApiPath";
import { StoreListRequestModel } from "api/merchantRepository/filterFeature/model/storeList/StoreListRequestModel";
import { StoreListResponseModel } from "api/merchantRepository/filterFeature/model/storeList/StoreListResponseModel";
import { PartnerManagementRequestModel } from "api/repositories/partnerManagement/model/PartnerManagementRequestModel";
import { PartnerManagementResponseModel } from "api/repositories/partnerManagement/model/PartnerManagementResponseModel";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { B2CUserInjection } from "features/b2cUserManagement/di/B2CUserInjection";
import { B2CUserActionRequestEntity } from "features/b2cUserManagement/domain/entities/B2CUserActionRequestEntity";
import { B2CUserRequestEntity } from "features/b2cUserManagement/domain/entities/B2CUserRequestEntity";
import { B2CUserResponseEntity } from "features/b2cUserManagement/domain/entities/B2CUserResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { UserTypeEnum } from "utils/enums/UserTypeEnum";
import { fetchFilterList } from "utils/format/FeatureFormat";
import { strings } from "utils/localizedStrings";

class StaffManagementInput implements Disposable {
  public fetchStaff = new BehaviorSubject<B2CUserRequestEntity>({});
  public fetchDetail = new BehaviorSubject<B2CUserRequestEntity>({});
  public fetchStaffParent = new BehaviorSubject<{
    params?: B2CUserRequestEntity;
    type?: "parentList" | "parentModal";
    typeOfUser?: UserTypeEnum;
  }>({});
  public fetchB2CPartner = new BehaviorSubject<PartnerManagementRequestModel>(
    {},
  );
  public loading = new BehaviorSubject<boolean>(false);
  public storeLoading = new BehaviorSubject<string>("");
  public loadStore = new BehaviorSubject<{
    params?: StoreListRequestModel;
    type?: "list" | "modal";
    typeOfUser?: UserTypeEnum;
  }>({});
  public fetchActions = new BehaviorSubject<{
    body?: B2CUserActionRequestEntity;
    action?: "create" | "update" | "delete";
  }>({});

  [Symbol.dispose](): void {
    this.fetchStaff.complete();
    this.loading.complete();
    this.loadStore.complete();
    this.storeLoading.complete();
    this.fetchB2CPartner.complete();
    this.fetchStaffParent.complete();
    this.fetchActions.complete();
  }
}

class StaffManagementOutput implements Disposable {
  public savedStaff = new BehaviorSubject<Array<B2CUserResponseEntity>>([]);
  public savedDetail = new BehaviorSubject<B2CUserResponseEntity>({});
  public savedStore = new BehaviorSubject<Array<StoreListResponseModel>>([]);
  public savedStoreForModal = new BehaviorSubject<
    Array<StoreListResponseModel>
  >([]);
  public savedB2CPartner = new BehaviorSubject<
    Array<PartnerManagementResponseModel>
  >([]);
  public savedStaffParent = new BehaviorSubject<Array<B2CUserResponseEntity>>(
    [],
  );
  public savedStaffParentForModal = new BehaviorSubject<
    Array<B2CUserResponseEntity>
  >([]);
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onUpdateSuccess = new BehaviorSubject<boolean>(false);
  public onActionsSuccess = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.savedStaff.complete();
    this.savedStoreForModal.complete();
    this.savedStaffParentForModal.complete();
    this.savedStore.complete();
    this.pagination.complete();
    this.onError.complete();
    this.savedB2CPartner.complete();
    this.savedStaffParent.complete();
    this.onUpdateSuccess.complete();
    this.onActionsSuccess.complete();
  }
}

@injectable()
export class StaffManagementViewModel extends BaseViewModel<
  StaffManagementInput,
  StaffManagementOutput
> {
  constructor() {
    super(new StaffManagementInput(), new StaffManagementOutput());
  }
  private useCase = B2CUserInjection.getUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: B2CUserRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : `createdAt DESC`;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchStaff, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.useCase.getList(params, USER_STAFF.LIST);
        if (res?.data) {
          this.output.savedStaff.next(res?.data);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.loadStore, async (props) => {
      if (props?.typeOfUser === UserTypeEnum.staff) {
        this.input.storeLoading.next(props?.type);
        return await fetchFilterList<
          StoreListResponseModel,
          StoreListRequestModel
        >({
          dataState: (value) =>
            props?.type === "list"
              ? this.output.savedStore.next(value)
              : this.output.savedStoreForModal.next(value),
          path: FILTER_FEATURE.STORE_LIST,
          isForceRefresh: true,
          isNeedFeature: true,
          params: props?.params,
        })?.finally(() => this.input.storeLoading.next(""));
      }
    });

    this.skipFirstInitial(this.input.fetchB2CPartner, async (params) => {
      try {
        return await fetchFilterList<
          PartnerManagementResponseModel,
          PartnerManagementRequestModel
        >({
          dataState: (value) => this.output.savedB2CPartner.next(value),
          path: PARTNER_MANAGEMENT.LIST,
          isForceRefresh: true,
          isNeedFeature: true,
          savedKey: CacheKeyEnum.b2cPartner,
          params,
        });
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchStaffParent, async (props) => {
      if (props?.typeOfUser === UserTypeEnum.staff) {
        this.input.storeLoading.next(props?.type);
        return await fetchFilterList<
          B2CUserResponseEntity,
          B2CUserRequestEntity
        >({
          dataState: (value) =>
            props?.type === "parentList"
              ? this.output.savedStaffParent.next(value)
              : this.output.savedStaffParentForModal.next(value),
          path: USER_STORE.LIST,
          isForceRefresh: true,
          isNeedFeature: true,
          params: props?.params,
        })?.finally(() => this.input.storeLoading.next(""));
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.useCase.getDetail(params, USER_STAFF.DETAIL);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      let { action, body } = props;
      switch (action) {
        case "create":
          try {
            const res = await this.useCase?.createUser(body, USER_STAFF.CREATE);
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.CreateSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
        case "update":
          try {
            const res = await this.useCase?.updateUser(body, USER_STAFF.UPDATE);
            if (res?.data) {
              this.output.onUpdateSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
        case "delete":
          try {
            this.input.loading.next(true);
            const res = await this.useCase?.deleteUser(
              { phone: body?.phone },
              USER_STAFF.DELETE,
            );
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.DeleteSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
          break;
      }
    });
  }
}
